import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Chip,
  InputAdornment,
  MenuItem,
  Autocomplete,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LocalHospital as HospitalIcon,
  FilterAlt as FilterIcon,
} from "@mui/icons-material";
import ReferralsTable from "../components/ReferralsTable";
import {
  getReferralsList,
  deleteReferral,
  getReferralCountsByLocation,
} from "../services/specialistReferralsService";
import {
  getStatuses,
  getLocations,
  getEmployees,
} from "../../../utils/services/control/masterDataService";

export default function SpecialistReferralsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [referrals, setReferrals] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Catalogs from API
  const [urgencyLevels, setUrgencyLevels] = useState([]);
  const [referralStatuses, setReferralStatuses] = useState([]);
  const [followupStatuses, setFollowupStatuses] = useState([]);
  const [locationsList, setLocationsList] = useState([]);
  const [locationCounts, setLocationCounts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [mdStatusOptions, setMdStatusOptions] = useState([]);
  const [referralRequestOptions, setReferralRequestOptions] = useState([]);
  const [catalogsLoaded, setCatalogsLoaded] = useState(false);

  // Filtros — se separa el "borrador" (lo que el usuario escribe/elige) del
  // "aplicado" (lo que realmente se manda a la API). El borrador solo se
  // vuelca al aplicado al presionar "Apply Filters"; no hay carga automática.
  const [selectedLocationId, setSelectedLocationId] = useState("");

  // Borrador — vinculado a los inputs de la barra de filtros
  const [searchDraft, setSearchDraft] = useState("");
  const [followUpDraft, setFollowUpDraft] = useState("");
  const [urgencyDraft, setUrgencyDraft] = useState("");
  const [statusDraft, setStatusDraft] = useState("");
  const [createdByDraft, setCreatedByDraft] = useState(null); // employee object
  const [mdStatusDraft, setMdStatusDraft] = useState("");
  const [referralRequestDraft, setReferralRequestDraft] = useState("");

  // Aplicado — lo que dispara la carga desde la API ("" significa "All")
  const [searchText, setSearchText] = useState("");
  const [followUpFilter, setFollowUpFilter] = useState("");
  const [urgencyFilter, setUrgencyFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [createdByFilter, setCreatedByFilter] = useState(""); // employeeId resuelto
  const [mdStatusFilter, setMdStatusFilter] = useState("");
  const [referralRequestFilter, setReferralRequestFilter] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, referral: null });

  // Load catalogs on mount
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        // Los conteos por location se cargan en un efecto aparte (loadLocationCounts)
        // porque dependen de los filtros activos.
        const [urgency, status, followup, locs, emps, mdStatus, referralReq] =
          await Promise.all([
            getStatuses("specialist_referral_urgency"),
            getStatuses("specialist_referral_status"),
            getStatuses("specialist_referral_followup"),
            getLocations({ clinical: true, status: 1 }),
            getEmployees({ status: 1 }),
            getStatuses("medical_director_status", false),
            getStatuses("specialist_referral_request", false),
          ]);
        setUrgencyLevels(urgency || []);
        setReferralStatuses(status || []);
        setFollowupStatuses(followup || []);
        setLocationsList(locs || []);
        setEmployees(emps || []);
        setMdStatusOptions(mdStatus || []);
        setReferralRequestOptions(referralReq || []);
      } catch (err) {
        console.error("Error loading catalogs:", err?.message);
      } finally {
        setCatalogsLoaded(true);
      }
    };
    loadCatalogs();
  }, []);

  // Filtros aplicados comunes al listado y al conteo por location (NO incluye
  // locationId — en el conteo se cuenta por cada location — ni paginación).
  const buildBaseFilters = useCallback(() => {
    const f = {};
    if (urgencyFilter) f.urgencyLevelId = urgencyFilter;
    if (statusFilter) f.referralStatusId = statusFilter;
    if (followUpFilter) f.followupStatusId = followUpFilter;
    if (createdByFilter) f.numEmployeeCreate = createdByFilter;
    if (mdStatusFilter) f.medicalDirectorStatusId = mdStatusFilter;
    if (referralRequestFilter) f.referralRequestId = referralRequestFilter;
    return f;
  }, [
    urgencyFilter,
    statusFilter,
    followUpFilter,
    createdByFilter,
    mdStatusFilter,
    referralRequestFilter,
  ]);

  // Load referrals from API
  const loadReferrals = useCallback(async () => {
    setLoading(true);
    try {
      const filters = { ...buildBaseFilters(), page, pageSize };
      if (selectedLocationId) filters.locationId = selectedLocationId;
      // searchText is handled client-side since API doesn't have text search param

      const result = await getReferralsList(filters);
      setReferrals(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error("Error loading referrals:", err?.message);
      setReferrals([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildBaseFilters, selectedLocationId, page, pageSize]);

  // Conteos por location — reflejan los filtros activos (sin locationId). Se
  // recargan cuando cambian esos filtros, no cuando cambia la location elegida
  // ni la página.
  const loadLocationCounts = useCallback(async () => {
    try {
      const counts = await getReferralCountsByLocation(buildBaseFilters());
      setLocationCounts(counts || []);
    } catch (err) {
      console.error("Error loading location counts:", err?.message);
    }
  }, [buildBaseFilters]);

  // Load on mount and when filters change
  useEffect(() => {
    if (catalogsLoaded) loadReferrals();
  }, [loadReferrals, catalogsLoaded]);

  // Recarga los conteos del sidebar al montar y cuando cambian los filtros.
  useEffect(() => {
    if (catalogsLoaded) loadLocationCounts();
  }, [loadLocationCounts, catalogsLoaded]);

  // Check for refresh trigger from create/edit page
  useEffect(() => {
    if (location.state?.refresh) {
      loadReferrals();
      if (location.state?.message) {
        setSnackbar({
          open: true,
          message: location.state.message,
          severity: "success",
        });
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state, loadReferrals]);

  // Client-side search filter on already-loaded data
  const displayedReferrals = useMemo(() => {
    if (!searchText.trim()) return referrals;
    const search = searchText.toLowerCase();
    return referrals.filter(
      (r) =>
        r.patientName?.toLowerCase().includes(search) ||
        r.cipId?.toLowerCase().includes(search) ||
        r.specialistName?.toLowerCase().includes(search)
    );
  }, [referrals, searchText]);

  const handleNewReferral = () => {
    navigate("/specialist-referrals/create");
  };

  const handleSelectReferral = (referral) => {
    navigate(`/specialist-referrals/edit/${referral.specialistReferralId}`);
  };

  const handleDeleteReferral = (referral) => {
    setDeleteDialog({ open: true, referral });
  };

  const handleConfirmDelete = async () => {
    const { referral } = deleteDialog;
    setDeleteDialog({ open: false, referral: null });
    try {
      await deleteReferral(referral.specialistReferralId);
      setSnackbar({
        open: true,
        message: `Referral for ${referral.patientName} deleted successfully.`,
        severity: "success",
      });
      loadReferrals();
    } catch (err) {
      console.error("Error deleting referral:", err?.message);
      setSnackbar({
        open: true,
        message: "Error deleting referral. Please try again.",
        severity: "error",
      });
    }
  };

  // Vuelca el borrador de filtros a los aplicados (dispara la carga desde la
  // API). El filtro "Created By" guarda el objeto empleado seleccionado en
  // el Autocomplete; aquí se extrae su employeeId, que es lo que espera el
  // endpoint. NO toca la location (es un filtro independiente del sidebar).
  const commitDraftFilters = () => {
    setSearchText(searchDraft);
    setFollowUpFilter(followUpDraft);
    setUrgencyFilter(urgencyDraft);
    setStatusFilter(statusDraft);
    setCreatedByFilter(createdByDraft ? createdByDraft.employeeId : "");
    setMdStatusFilter(mdStatusDraft);
    setReferralRequestFilter(referralRequestDraft);
    setPage(1);
  };

  const handleApplyFilters = () => commitDraftFilters();

  // La location es un filtro inmediato del sidebar. Al cambiarla también se
  // aplican los filtros que el usuario tenga seleccionados en la barra, para
  // que ambos viajen juntos a la API (antes el cambio de location ignoraba los
  // filtros aún en borrador y el resultado quedaba inconsistente).
  const handleSelectLocation = (locationId) => {
    setSelectedLocationId(locationId);
    commitDraftFilters();
  };

  const handleResetFilters = () => {
    setSelectedLocationId("");
    // borrador
    setSearchDraft("");
    setFollowUpDraft("");
    setUrgencyDraft("");
    setStatusDraft("");
    setCreatedByDraft(null);
    setMdStatusDraft("");
    setReferralRequestDraft("");
    // aplicado
    setSearchText("");
    setFollowUpFilter("");
    setUrgencyFilter("");
    setStatusFilter("");
    setCreatedByFilter("");
    setMdStatusFilter("");
    setReferralRequestFilter("");
    setPage(1);
  };

  // Build sidebar centers list with active-referral counts (Cambio 2).
  // The count endpoint is the source of truth; falls back to the plain
  // locations list if the count endpoint returned nothing.
  const centersList = useMemo(() => {
    if (locationCounts.length > 0) {
      const totalCount = locationCounts.reduce((sum, c) => sum + (c.referralCount || 0), 0);
      return [
        { locationId: "", name: "All Centers", count: totalCount },
        ...locationCounts.map((c) => ({
          locationId: c.locationId,
          name: c.medicalCenter,
          count: c.referralCount || 0,
        })),
      ];
    }
    return [
      { locationId: "", name: "All Centers", count: null },
      ...locationsList.map((loc) => ({
        locationId: loc.locationId,
        name: loc.medicalCenter,
        count: null,
      })),
    ];
  }, [locationCounts, locationsList]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Specialist Referrals
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage patient referrals to specialists
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewReferral}
          size="large"
        >
          New Referral
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <TextField
              placeholder="Search by member name..."
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleApplyFilters(); }}
              size="small"
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Follow Up"
              value={followUpDraft}
              onChange={(e) => setFollowUpDraft(e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All</MenuItem>
              {followupStatuses.map((s) => (
                <MenuItem key={s.statusId} value={s.statusId}>
                  {s.statusName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Urgency"
              value={urgencyDraft}
              onChange={(e) => setUrgencyDraft(e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All</MenuItem>
              {urgencyLevels.map((l) => (
                <MenuItem key={l.statusId} value={l.statusId}>
                  {l.statusName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              value={statusDraft}
              onChange={(e) => setStatusDraft(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All</MenuItem>
              {referralStatuses.map((s) => (
                <MenuItem key={s.statusId} value={s.statusId}>
                  {s.statusName}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {/* Cambio 2 — filtros nuevos */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Autocomplete
              size="small"
              sx={{ minWidth: 220 }}
              options={employees}
              value={createdByDraft}
              onChange={(_e, emp) => setCreatedByDraft(emp)}
              getOptionLabel={(emp) =>
                emp ? `${emp.firstName || ""} ${emp.lastName || ""}`.trim() : ""
              }
              isOptionEqualToValue={(option, value) => option.employeeId === value?.employeeId}
              renderInput={(params) => (
                <TextField {...params} label="Created By" placeholder="All" />
              )}
            />
            <TextField
              select
              label="Medical Director Status"
              value={mdStatusDraft}
              onChange={(e) => setMdStatusDraft(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All</MenuItem>
              {mdStatusOptions.map((s) => (
                <MenuItem key={s.statusId} value={s.statusId}>
                  {s.statusName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Referral Request"
              value={referralRequestDraft}
              onChange={(e) => setReferralRequestDraft(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All</MenuItem>
              {referralRequestOptions.map((s) => (
                <MenuItem key={s.statusId} value={s.statusId}>
                  {s.statusName}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<FilterIcon />}
              onClick={handleApplyFilters}
              size="small"
            >
              Apply Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleResetFilters}
              size="small"
            >
              Reset
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Main Content */}
      <Box sx={{ display: "flex", gap: 3 }}>
        {/* Sidebar - Medical Centers */}
        <Paper sx={{ width: 220, p: 2, flexShrink: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
            <HospitalIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: "middle" }} />
            Medical Centers
          </Typography>
          <Stack spacing={1}>
            {centersList.map((center) => (
              <Chip
                key={center.locationId || "all"}
                label={
                  center.count != null
                    ? `${center.name} (${center.count})`
                    : center.name
                }
                onClick={() => handleSelectLocation(center.locationId)}
                variant={selectedLocationId === center.locationId ? "filled" : "outlined"}
                color={selectedLocationId === center.locationId ? "primary" : "default"}
                sx={{
                  justifyContent: "flex-start",
                  "& .MuiChip-label": { flex: 1 },
                }}
              />
            ))}
          </Stack>
        </Paper>

        {/* Table */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ReferralsTable
            data={displayedReferrals}
            loading={loading}
            total={searchText.trim() ? displayedReferrals.length : total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onSelectReferral={handleSelectReferral}
            onDeleteReferral={handleDeleteReferral}
          />
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, referral: null })}
      >
        <DialogTitle>Delete Referral</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the referral for{" "}
            <strong>{deleteDialog.referral?.patientName}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, referral: null })}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
